import { Box } from '@oresundsbron/design-system';
import { RecursivePartial, ValidationResult } from '@oresundsbron/use-form';
import {
  PrivateITicketOrderInputV2,
  privateITicketOrderV2,
} from '@oresundsbron/validators';
import { pipe } from 'fp-ts/lib/function';
import {
  chain,
  fromPredicate,
  fromTask,
  getOrElseW,
  map,
} from 'fp-ts/lib/TaskOption';
import { of } from 'fp-ts/lib/Task';
import { Trans, useTranslation } from 'next-i18next';
import { useCallback, useMemo } from 'react';
import { useCountriesOptGroups } from '../../hooks/useCountries';
import { validateT } from '../../lib/zod';
import { Data, OnlineTicketForm } from './Form';
import { setData } from './stores/usePrivateITicketData';
import { useValidateAlpr } from '../../hooks/useValidateAlpr';
import { chainValidations, isFieldErrorEmpty } from '../../lib/errors';
import { pick } from '../../lib/data';
import { PrivacyPolicyLink } from '../../components/Links/PrivacyPolicyLink';
import { TermsLink } from '../../components/Links/TermsLink';
import { veColorList } from '~/utils';
import { useEmissionClasses } from '~/hooks/useEmissionClasses';
import { useITicketTypes } from '~/hooks/useITicketTypes';
import { useCountriesBvSupport } from '@oresundsbron/app-common';

const pickLicensePlateAndCountry = pick<Data>()('licensePlate', 'country');

export const OnlineTicketOrder = () => {
  const { t } = useTranslation(['onlineTicket', 'common'], {
    nsMode: 'fallback',
  });
  const { data: ticketTypes } = useITicketTypes();
  const validateAlpr = useValidateAlpr();
  const bvSupportCountries = useCountriesBvSupport();
  const { fuelTypes, emissionClasses } = useEmissionClasses();

  const countries = useCountriesOptGroups();

  const onSubmit = useCallback((data: Data) => {
    return pipe(
      {
        ...data,
        refundUrl: `${window.location.origin}${window.location.pathname}`,
      } as PrivateITicketOrderInputV2,
      setData
    );
  }, []);

  const pickAndValidateAlpr = useCallback(
    (data: Data) => (errors?: ValidationResult<Data>) =>
      pipe(
        errors,
        fromPredicate(isFieldErrorEmpty('licensePlate')),
        map(() => data),
        map(pickLicensePlateAndCountry),
        chain(fromPredicate((x) => !!x.country && !!x.licensePlate)),
        chain(
          validateAlpr<Data>({
            licensePlate: t('form.validations.licensePlate.exist'),
          })
        )
      ),
    [t, validateAlpr]
  );

  const validate = useCallback(
    (data: Data) =>
      pipe(
        data,
        validateT(privateITicketOrderV2.spa, t),
        fromTask,
        chainValidations(pickAndValidateAlpr(data)),
        getOrElseW(() => of(undefined))
      )(),
    [pickAndValidateAlpr, t]
  );

  const values = useMemo(
    () =>
      ({
        amount: '1',
      } as RecursivePartial<Data>),
    []
  );

  return (
    <OnlineTicketForm
      onSubmit={onSubmit}
      sections={{
        car: {
          title: t('form.sections.car.title'),
          description: t('form.sections.car.description'),
        },
        ticketType: {
          title: t('form.sections.ticketType.title'),
        },
        contactInformation: {
          title: t('form.sections.contactInformation.title'),
          description: t('form.sections.contactInformation.description'),
        },
        news: {
          title: t('form.sections.news.title'),
          description: t('form.sections.news.description'),
        },
        termsOfAgreement: {
          title: t('form.sections.termsOfAgreement.title'),
          orderTitle: t('form.sections.termsOfAgreement.orderTitle'),
          description: (
            <Trans
              ns="onlineTicket"
              i18nKey="form.sections.termsOfAgreement.description"
            >
              <Box as="span" display="block" mb="spacing.100"></Box>
              <TermsLink type="online_ticket" />
              <PrivacyPolicyLink />
            </Trans>
          ),
        },
      }}
      defaultValues={values}
      labels={{
        vehicleOption: t('form.labels.vehicleType'),
        country: t('form.labels.country'),
        licensePlate: t('form.labels.licensePlate'),
        email: t('form.labels.email'),
        firstName: t('form.labels.firstName'),
        lastName: t('form.labels.lastName'),
        subscribe: t('form.labels.subscribe'),
        submit: t('form.labels.submit'),
        agreement: t('form.labels.termsOfAgreement'),
        loading: t('form.labels.loading'),
        total: t('form.labels.total'),
        noVehicleInfo: t('form.validations.licensePlate.notfound'),
        loadingSearch: t('action.loading'),
        vehicleSearch: t('action.search'),
        make: t('form.labels.make'),
        model: t('form.labels.model'),
        color: t('form.labels.color'),
        licensePlateExist: t('form.validations.licensePlate.exist'),
        chooseCountry: t('form.placeholders.country'),
      }}
      vehicleInfoLabels={{
        make: t('form.labels.make'),
        model: t('form.labels.model'),
        color: t('form.labels.color'),
        fuelType: t('form.labels.fuelType'),
        emissionClass: t('form.labels.emissionClass'),
        weightClass: t('form.labels.weightClass'),
        hybrid: t('form.labels.hybrid'),
        weightClasses: {
          heavy: t(`weightClasses.heavy`),
          light: t(`weightClasses.light`),
        },
        hybridLabel: {
          true: t(`hybrid.true`),
          false: t(`hybrid.false`),
        },
        vehicleInformationTitle: t('form.labels.vehicleInformationTitle'),
      }}
      vehicleColors={veColorList.map((value) => ({
        label: t(`vehicleColors.${value}`),
        value,
      }))}
      defaultVehicleColor={t(`vehicleColors.other`)}
      fuelTypes={fuelTypes}
      emissionClasses={emissionClasses}
      countryOptions={countries}
      bvSupportCountries={bvSupportCountries}
      ticketTypes={ticketTypes || []}
      onValidate={validate}
    />
  );
};
